import React from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box, Divider, Paper, Stack, Typography } from '@mui/material';

type MatchStrength = 'exact' | 'strong' | 'weak';

const MATCH_STRENGTH_CONFIG: Record<MatchStrength, { label: string; color: string }> = {
  exact: { label: 'Exact', color: '#4caf50' },
  strong: { label: 'Strong', color: '#4caf50' },
  weak: { label: 'Weak', color: '#ffa726' },
};

function resolveMatchStrength(score: number | null | undefined): MatchStrength | null {
  if (score === null || score === undefined) return null;
  if (score === 1) return 'exact';
  if (score >= 0.7) return 'strong';
  return 'weak';
}

interface MatchSubsectionProps {
  title: string;
  document: string | null | undefined;
  matchedRecord: string | null | undefined;
  score: number | null | undefined;
}

const MatchSubsection = ({ title, document, matchedRecord, score }: MatchSubsectionProps) => {
  const strength = resolveMatchStrength(score);
  const config = strength ? MATCH_STRENGTH_CONFIG[strength] : null;

  const rows = [
    { label: 'Document', value: document },
    { label: 'Matched Record', value: matchedRecord },
  ].filter(r => r.value !== null && r.value !== undefined);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" fontWeight="bold">
          {title}
        </Typography>
        {config && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <CheckCircleOutlineIcon sx={{ fontSize: 16, color: config.color }} />
            <Typography variant="body2" sx={{ color: config.color }}>
              {config.label}
            </Typography>
          </Stack>
        )}
      </Stack>

      {rows.length > 0 && (
        <Stack sx={{ mt: 1 }}>
          {rows.map(({ label, value }, index) => (
            <React.Fragment key={label}>
              {index > 0 && <Divider />}
              <Stack direction="row" spacing={1} sx={{ py: 0.75 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                  {label}
                </Typography>
                <Typography variant="body2">{value}</Typography>
              </Stack>
            </React.Fragment>
          ))}
        </Stack>
      )}
    </Box>
  );
};

interface MatchingDetailsSectionProps {
  payload: Record<string, unknown> | null | undefined;
}

export const MatchingDetailsSection = ({ payload }: MatchingDetailsSectionProps) => {
  if (!payload) return null;

  const parsedData = payload.parsedData as Record<string, unknown> | null | undefined;
  const matchingDetails = payload.matchingDetails as Record<string, unknown> | null | undefined;

  const borrowerDocument = parsedData?.applicantName as string | null | undefined;
  const borrowerMatchedRecord = matchingDetails?.borrowerName as string | null | undefined;
  const borrowerScore = matchingDetails?.borrowerScore as number | null | undefined;

  const lenderDocument = parsedData?.lenderName as string | null | undefined;
  const lenderMatchedRecord = matchingDetails?.lenderName as string | null | undefined;
  const lenderScore = matchingDetails?.lenderScore as number | null | undefined;

  const guarantorDocument = parsedData?.guarantorName as string | null | undefined;
  const guarantorMatchedRecord = (matchingDetails?.guarantorName as string | null | undefined) ?? '—';
  const guarantorScore = matchingDetails?.guarantorScore as number | null | undefined;

  const hasContent =
    borrowerDocument ||
    borrowerMatchedRecord ||
    borrowerScore !== undefined ||
    lenderDocument ||
    lenderMatchedRecord ||
    lenderScore !== undefined ||
    guarantorDocument;

  if (!hasContent) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Matching Details
      </Typography>
      <Stack spacing={2} divider={<Divider />}>
        <MatchSubsection
          title="Borrower"
          document={borrowerDocument}
          matchedRecord={borrowerMatchedRecord}
          score={borrowerScore}
        />
        <MatchSubsection
          title="Lender"
          document={lenderDocument}
          matchedRecord={lenderMatchedRecord}
          score={lenderScore}
        />
        {guarantorDocument && (
          <MatchSubsection
            title="Guarantor"
            document={guarantorDocument}
            matchedRecord={guarantorMatchedRecord}
            score={guarantorScore}
          />
        )}
      </Stack>
    </Paper>
  );
};
